import { Icon } from '@iconify/react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import {
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';

interface UnsavedChangesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirmClose: () => void;
}

export const UnsavedChangesDialog = ({
  open,
  onOpenChange,
  onConfirmClose,
}: UnsavedChangesDialogProps) => {
  const { t } = useTranslation(['knowledge_base', 'common']);

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 z-20" />
      <DialogPrimitive.Content
        className="absolute inset-0 flex items-center justify-center p-0 z-30 focus:outline-none"
        role="alertdialog"
        aria-modal="true"
        onEscapeKeyDown={(e) => e.stopPropagation()}
      >
        <div className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg">
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Icon
                icon="mingcute:alert-fill"
                className="inline bg-red-100 rounded-lg text-red-500 p-2 box-content"
              />
              {t('knowledge_base:article.close_modal.title', { defaultValue: 'Discard changes?' })}
            </AlertDialogTitle>
          </AlertDialogHeader>

          <AlertDialogDescription>
            {t('knowledge_base:article.close_modal.subtitle', {
              defaultValue: 'You have unsaved changes. Leaving will discard them.',
            })}
          </AlertDialogDescription>

          <Separator className="my-7" />

          <AlertDialogFooter>
            <AlertDialogCancel asChild>
              <Button size="md" variant="secondary">
                {t('knowledge_base:article.close_modal.cancel', { defaultValue: 'Cancel' })}
              </Button>
            </AlertDialogCancel>

            <Button variant="danger" size="md" onClick={onConfirmClose}>
              {t('knowledge_base:article.close_modal.continue', { defaultValue: 'Discard' })}
            </Button>
          </AlertDialogFooter>
        </div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Root>
  );
};
